import { ClientActionCacheService } from "@cervval.js/client-action/src/services/client.action.cache.service";
import { ClientActionService } from "@cervval.js/client-action/src/services/client.action.service";
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CjsDialog, DialogPreConfig, DialogSize, showErrorInDialog } from "@cervval.js/client-html5-ui/src/components/dialog/dialog";
import { ACTION_SERVICE } from "@cervval.js/common-action/src/services/action.service";
import { ACTION_CACHE_SERVICE } from "@cervval.js/common-action/src/services/cache.service";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { NOW } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { CAPACITOR_SERVICE, CapacitorService } from "@ehealth_tools/client-capacitor/src/services/capacitor.service";
import { ID } from "@ehealth_tools/common/src/data/propertyTypes/property.types";
import { CGV_PRO } from "@sportsante/common-shared/src/data/cgv/cgv";
import { FetchDataTypes } from "@sportsante/common-shared/src/data/global/fetchData.config";
import { ElementTypes } from "@sportsante/common-shared/src/data/global/global.model";
import { AppLoginData } from "@sportsante/common-shared/src/data/users/appLoginData";
import { AcceptTerms } from "./acceptTerms";
import { i18nAcceptTerm } from "./acceptTerms.i18n";

/**
 * If given user has not accepted the terms of service of Activmed, a dialog will be automatically prompted in order for the user to accept it,
 * or to be logged out if he doesn't accept
 * @param userId Id of the user
 * @requires ClientActionService, ClientActionCacheService and CapacitorService
 */
export function promptTermsAcceptationInDialogIfNeeded(userId: ID): void {

    // TODO replace by injector.get as soon as CapacitorService is used by all client apps
    const capacitorService = injector.find<CapacitorService<AppLoginData, ElementTypes, FetchDataTypes>>(CAPACITOR_SERVICE);
    const cacheService = injector.get<ClientActionCacheService<ElementTypes, FetchDataTypes>>(ACTION_CACHE_SERVICE);
    const actionService = injector.get<ClientActionService<ElementTypes, unknown>>(ACTION_SERVICE);
    const i18n = i18nAcceptTerm();

    const userInCache = cacheService.getDTOCacheRepository("user_profiles").getDTOById(userId);
    if (userInCache.consentTs != null) { return; }

    const dialog = new CjsDialog({
        title: i18n.dialogTitle,
        size: DialogSize.EXTRA_LARGE,
        preConfig: DialogPreConfig.NO_BUTTON,
        enableDismiss: true,
        onCloseButtonClick: async () => {
            // TODO remove if-bloc as soon as CapacitorService is used by all client apps
            if (capacitorService == null) {
                window.location.href = "/logout";
            } else {
                await capacitorService.logout();
            }
            return { preventClose: false };
        },
        openOnInit: false,
        enableDraggable: false
    });

    dialog.classList.add("dialog-accept-terms");

    dialog.addToBody(
        createHtmlElement("div", { innerHTML: i18n.termsPrefix, cssClass: "terms-prefix" })
    );

    const acceptTerms = new AcceptTerms({
        terms: CGV_PRO,
        acceptCallback: () => {
            // Disable checkbox and button in case the network is slow to prevent user from interacting again
            acceptTerms.disableElements();
            actionService.encapsulateInAction(
                (action) => {
                    action.updateValueIfNeeded("user_profiles", userInCache, "consentTs", NOW());
                },
                {
                    logMessage: "Set consent for user"
                }
            ).then(() =>
                dialog.hide()
            ).catch(
                showErrorInDialog
            ).finally(() => {
                // Refresh terms in order to reset components behavior
                acceptTerms.refresh();
            });
        }
    });

    dialog.addToBody(
        acceptTerms
    );

    dialog.show();
}